import React, { useEffect, useState } from "react";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { IonSpinner } from "@ionic/react";

type ImageProps<T> = {
  src: string | undefined;
  width?: string;
  maxWidth?: string;
  isLoading?: boolean;
  setIsLoading?: (value: boolean) => void;
  onClick?: () => void;
  profile?: boolean;
  avatar?: boolean;
};

const Image = <T extends { [key: string]: any }>({
  src,
  width,
  maxWidth,
  onClick,
  isLoading = false,
  setIsLoading = undefined,
  profile = false,
  avatar = false,
}: ImageProps<T>) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [localLoading, setLocalLoading] = useState<boolean>(false); // Track local loading state

  const fetchSignedUrl = async (image_url: string | undefined) => {
    if (!image_url) {
      setHasError(true); // No image URL provided
      setIsImageLoaded(true);
      return null;
    }
    const regex = /https:\/\/storage\.googleapis\.com\/[^\/]+\/(.+)/;
    const match = image_url.match(regex);
    const imagePath = match ? match[1] : "";

    if (imagePath) {
      const storage = getStorage();
      const storageRef = ref(storage, imagePath);

      try {
        const url = await getDownloadURL(storageRef);
        return url;
      } catch (error) {
        console.error("Error fetching signed URL:", error);
        setHasError(true); // Set error if URL fetching fails
      }
    }

    return null;
  };

  const onImageClick = () => {
    if (onClick) {
      if (setIsLoading) setIsLoading(true);
      onClick();
    }
  };

  useEffect(() => {
    const fetchUrl = async () => {
      setIsImageLoaded(false);
      setHasError(false);
      setLocalLoading(true); // Start loading

      if (setIsLoading) setIsLoading(true); // Set the external loading state
      setSignedUrl(null); // Reset the signed URL
      const url = await fetchSignedUrl(src);

      setSignedUrl(url);
      if (!url) {
        // If there was no URL or it failed to load, set the fallback image as loaded
        setIsImageLoaded(true);
        setLocalLoading(false);
        if (setIsLoading) setIsLoading(false);
      }
    };

    fetchUrl();
  }, [src]);

  const handleImageLoad = () => {
    setIsImageLoaded(true); // Mark image as loaded
    setLocalLoading(false); // End local loading
    if (setIsLoading) setIsLoading(false); // End external loading
  };

  const handleImageError = () => {
    setIsImageLoaded(true); // Mark image as loaded even if there’s an error
    setHasError(true); // Mark as error
    setLocalLoading(false); // End local loading
    if (setIsLoading) setIsLoading(false); // End external loading
  };

  const outerDivStyle = {
    width,
    maxHeight: width,
    maxWidth,
    color: "var(--ion-color-dark)",
    backgroundColor: "var(--ion-color-light)",
  };

  const innerDivStyle = {
    width,
    paddingBottom: "100%",
    position: "relative" as const,
    border: "4px solid #ffffff",
    borderRadius: profile ? "50%" : "10px", // Adjust borderRadius based on profile
    overflow: "hidden",
    backgroundImage: signedUrl && !hasError
      ? "none"
      : "url('/assets/ai.jpg')", // Default image if no signed URL or error
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const imageStyle = {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  };

  const spinnerStyle = {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "var(--ion-color-dark)",
    backgroundColor: "var(--ion-color-light)",
  };

  // Spinner will show when localLoading is true or external isLoading is true, and image has not been loaded yet
  const shouldShowSpinner = (localLoading || isLoading) && !isImageLoaded && !hasError;

  return (
    <>
      {!avatar ? (
        <div style={outerDivStyle}>
          <div style={innerDivStyle}>
            {signedUrl && !hasError ? (
              <img
                src={signedUrl}
                style={imageStyle}
                onClick={onImageClick}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            ) : hasError ? (
              <>
                  {shouldShowSpinner ? (
                    <div style={spinnerStyle}>
                      <IonSpinner name="lines" color="dark" />
                    </div>
                  ) : (
                    <img src="/assets/ai.jpg" style={imageStyle} onClick={onImageClick} />
                  )
                }
              </>
            ) : null}
            

            {shouldShowSpinner && (
              <div style={spinnerStyle}>
                <IonSpinner name="lines" color="dark" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {signedUrl && !hasError ? (
            <img src={signedUrl} onClick={onImageClick} />
          ) : (
            <img src="/assets/ai.jpg" />
          )}
        </>
      )}
    </>
  );
};

export default Image;
